import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Terminal, ArrowRight, Check, Loader2, Eye, EyeOff } from "lucide-react";

type AuthMode = "login" | "register";

export default function JoinPage() {
  const [, setLocation] = useLocation();
  // back to where sign-in was asked for (a team invite: /join?redirect=/invite/<token>); same-site paths only
  const afterAuth = (): string => { const r = new URLSearchParams(window.location.search).get("redirect") || ""; return /^\/(?!\/)[\w\-/]*$/.test(r) ? r : "/"; };

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ?invite=<code> comes from the approval / invite email: open on "create account" and send the code with the sign-up
  const [inviteCode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("invite")?.trim() || ""; } catch { return ""; }
  });
  const [authMode, setAuthMode] = useState<AuthMode>(inviteCode ? "register" : "login");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);

  async function handleWaitlist(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch("/api/public/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Something went wrong");
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Failed to join waitlist");
    } finally {
      setLoading(false);
    }
  }

  async function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");
    setWaitlisted(false);
    setAuthLoading(true);

    try {
      if (authMode === "register") {
        const r = await fetch("/api/auth/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: authEmail.trim(), password: authPassword, name: authName.trim(), ...(inviteCode ? { inviteCode } : {}) }),
        });
        const data = await r.json();
        if (!r.ok) {
          throw new Error(data.error || "Registration failed");
        }
        if (data.waitlisted) {
          setWaitlisted(true);
          setAuthSuccess(data.message || "You're on the waitlist!");
        } else {
          window.location.href = afterAuth();
        }
      } else {
        const r = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: authEmail.trim(), password: authPassword }),
        });
        const data = await r.json();
        if (r.status === 403 && data.waitlisted) {
          setWaitlisted(true);
          setAuthSuccess("You're on the waitlist. We'll notify you when your spot is ready.");
        } else if (!r.ok) {
          throw new Error(data.error || "Login failed");
        } else {
          window.location.href = afterAuth();
        }
      }
    } catch (err: any) {
      setAuthError(err.message || "Something went wrong");
    } finally {
      setAuthLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-mono flex flex-col">
      <style>{`
        .neon-glow-cyan { text-shadow: 0 0 20px rgba(0, 255, 255, 0.8); }
        .neon-glow-purple { text-shadow: 0 0 20px rgba(168, 85, 247, 0.8); }
        .cursor-blink { animation: blink 1s step-end infinite; }
        @keyframes blink { from, to { opacity: 1; } 50% { opacity: 0; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
      `}</style>

      <header className="border-b border-white/5 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link href="/">
            <span className="text-xl font-black tracking-tighter text-cyan-400 neon-glow-cyan cursor-pointer">DRPR</span>
          </Link>
          <Link href="/blog">
            <span className="text-[11px] uppercase tracking-widest text-white/30 hover:text-white/60 transition-colors cursor-pointer">Blog</span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-white/25 mb-8">
            <Terminal size={12} className="text-cyan-400" />
            <span>Access Control Terminal</span>
            <span className="w-2 h-3 bg-cyan-400/50 cursor-blink ml-1"></span>
          </div>

          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter mb-3">
            DEPLOY YOUR<br /><span className="text-cyan-400 neon-glow-cyan">MARKETING TEAM</span>
          </h1>
          <p className="text-sm text-white/40 font-sans mb-12">
            One autonomous agent. Five specialists. Your marketing — running around the clock.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="border border-white/10 p-7 flex flex-col hover:border-cyan-400/40 transition-all group">
              <div className="text-[9px] uppercase tracking-[0.3em] text-cyan-400/60 mb-4 font-bold">New Here_</div>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2">Join the Waitlist</h2>
              <p className="text-[11px] text-white/35 font-sans mb-6 leading-relaxed">
                We're rolling out access gradually. Drop your email and we'll reach out when your spot is ready.
              </p>

              {submitted ? (
                <div className="fade-up flex items-start gap-3 bg-cyan-400/5 border border-cyan-400/20 p-4">
                  <Check size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-1">You're on the list</p>
                    <p className="text-[11px] text-white/40 font-sans">We'll be in touch when your spot opens up.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleWaitlist} className="flex flex-col gap-3 mt-auto">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-black border border-white/10 px-4 py-3 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-cyan-400/40 transition-colors"
                    data-testid="input-waitlist-name"
                  />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-black border border-white/10 px-4 py-3 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-cyan-400/40 transition-colors"
                    data-testid="input-waitlist-email"
                  />
                  {error && (
                    <p className="text-[11px] text-red-400 uppercase tracking-widest">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-cyan-400/10 border border-cyan-400/40 text-cyan-400 text-xs font-bold uppercase tracking-widest hover:bg-cyan-400 hover:text-black transition-all disabled:opacity-50"
                    data-testid="button-waitlist-submit"
                  >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : (
                      <>{`Request Access`} <ArrowRight size={12} /></>
                    )}
                  </button>
                </form>
              )}
            </div>

            <div className="border border-white/10 p-7 flex flex-col hover:border-purple-400/40 transition-all group">
              <div className="text-[9px] uppercase tracking-[0.3em] text-purple-400/60 mb-4 font-bold">Returning_</div>

              <div className="flex gap-0 mb-5">
                <button
                  onClick={() => { setAuthMode("login"); setAuthError(""); setAuthSuccess(""); setWaitlisted(false); }}
                  className={`flex-1 text-[9px] uppercase tracking-widest py-1.5 border transition-all font-bold ${authMode === "login" ? "border-purple-400/40 text-purple-400 bg-purple-400/10" : "border-white/10 text-white/30 hover:text-white/50"}`}
                  data-testid="tab-login"
                >
                  Sign In
                </button>
                <button
                  onClick={() => { setAuthMode("register"); setAuthError(""); setAuthSuccess(""); setWaitlisted(false); }}
                  className={`flex-1 text-[9px] uppercase tracking-widest py-1.5 border border-l-0 transition-all font-bold ${authMode === "register" ? "border-purple-400/40 text-purple-400 bg-purple-400/10" : "border-white/10 text-white/30 hover:text-white/50"}`}
                  data-testid="tab-register"
                >
                  Sign Up
                </button>
              </div>

              {waitlisted ? (
                <div className="fade-up flex items-start gap-3 bg-purple-400/5 border border-purple-400/20 p-4">
                  <Check size={14} className="text-purple-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-purple-400 uppercase tracking-widest mb-1">You're on the list</p>
                    <p className="text-[11px] text-white/40 font-sans">{authSuccess || "We'll notify you when your spot opens up."}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleAuth} className="flex flex-col gap-3">
                  {authMode === "register" && (
                    <input
                      type="text"
                      placeholder="Your name"
                      value={authName}
                      onChange={(e) => setAuthName(e.target.value)}
                      className="bg-black border border-white/10 px-4 py-3 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-400/40 transition-colors"
                      data-testid="input-auth-name"
                    />
                  )}
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    required
                    className="bg-black border border-white/10 px-4 py-3 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-400/40 transition-colors"
                    data-testid="input-auth-email"
                  />
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full bg-black border border-white/10 px-4 py-3 pr-10 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-400/40 transition-colors"
                      data-testid="input-auth-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                      data-testid="button-toggle-password"
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>

                  {authMode === "login" && (
                    <div className="text-right">
                      <Link href="/forgot-password">
                        <span className="text-[10px] text-white/30 hover:text-purple-400 transition-colors cursor-pointer uppercase tracking-widest" data-testid="link-forgot-password">
                          Forgot password?
                        </span>
                      </Link>
                    </div>
                  )}

                  {authError && (
                    <p className="text-[11px] text-red-400 uppercase tracking-widest" data-testid="text-auth-error">{authError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={authLoading}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-purple-400/10 border border-purple-400/40 text-purple-400 text-xs font-bold uppercase tracking-widest hover:bg-purple-400 hover:text-black transition-all disabled:opacity-50 mt-1"
                    data-testid="button-auth-submit"
                  >
                    {authLoading ? <Loader2 size={14} className="animate-spin" /> : (
                      <>{authMode === "login" ? "Sign In" : "Create Account"} <ArrowRight size={12} /></>
                    )}
                  </button>
                </form>
              )}

              <div className="mt-auto pt-4 space-y-2">
                <Link href="/signup" className="flex items-center justify-center gap-2 px-6 py-2.5 border border-white/5 text-white/20 text-[10px] font-bold uppercase tracking-widest hover:border-white/10 hover:text-white/40 transition-all">
                  Redeem Invite Code
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-[10px] text-white/20 uppercase tracking-widest">System online — all agents ready</span>
            </div>
            <div className="flex items-center gap-6">
              <Link href="/privacy"><span className="text-[10px] text-white/20 hover:text-white/40 transition-colors uppercase tracking-widest cursor-pointer">Privacy</span></Link>
              <Link href="/terms"><span className="text-[10px] text-white/20 hover:text-white/40 transition-colors uppercase tracking-widest cursor-pointer">Terms</span></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
